import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/lib/site";
import { motion, AnimatePresence } from "framer-motion";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-[#EAE7E0]"
          : "backdrop-blur-md bg-[#FDFBF7]/40 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 h-24 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <img
            src={COMPANY.logo}
            alt="Farmico"
            className="h-14 sm:h-16 w-auto object-contain transition-transform group-hover:scale-105"
          />
        </Link>

        <nav className="hidden lg:flex items-center bg-[#F1ECE2] rounded-full p-1.5 border border-[#EAE7E0]">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative px-4 xl:px-5 py-2 rounded-full text-[13px] font-semibold tracking-wide transition-all duration-300 ${
                  isActive
                    ? "bg-[#2A4D3B] text-[#FDFBF7] shadow-[0_2px_10px_-3px_rgba(42,77,59,0.55)]"
                    : "text-[#3A3530] hover:text-[#2A4D3B] hover:bg-white/60"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex">
          <Link
            to="/contact"
            data-testid="header-cta-contact"
            className="inline-flex items-center gap-2 rounded-full bg-[#2A4D3B] hover:bg-[#3A634E] text-[#FDFBF7] px-5 py-2.5 text-sm font-semibold transition-colors"
          >
            Request a Quote
            <span aria-hidden>→</span>
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden h-11 w-11 grid place-items-center rounded-full border border-[#EAE7E0] bg-white/70"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-[#EAE7E0] bg-[#FDFBF7]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-2">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-full text-sm font-semibold transition-colors ${
                      isActive
                        ? "bg-[#2A4D3B] text-[#FDFBF7]"
                        : "text-[#1A1A1A] hover:bg-[#F1ECE2]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-cta-contact"
                className="mt-4 inline-flex items-center justify-center rounded-full bg-[#2A4D3B] text-[#FDFBF7] px-5 py-3 text-sm font-semibold"
              >
                Request a Quote
              </Link>
              <p className="text-xs text-[#4A4540] mt-2">
                {COMPANY.phone} &middot; {COMPANY.email}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
